import React, {useCallback} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'
import {cloudPathName, parseCloudPath, tryParseCloudPath} from '@/shared/utils/cloudPath'
import {openFileInViewer} from '../external-viewer/open-in-viewer'
import {CLOUD_LINK_ICONS, cloudLinkKind} from './cloudLinkKind'

/**
 * Web location of a `cloud:` path (spec cloud-path.md, "Web viewer"): `/results?file=<rel>` for
 * files, `/files?path=<rel>` for folders. Used as the link's `href`, so a middle-click or "open in
 * new tab" lands on the same place the in-app click opens.
 */
export function cloudPathHref(cloudPath: string): string {
  const {rel, isFolder} = parseCloudPath(cloudPath)
  return isFolder
    ? `/files?path=${encodeURIComponent(rel)}`
    : `/results?file=${encodeURIComponent(rel)}`
}

/** Opens a `cloud:` path: files in the Results viewer, folders in the Files panel. */
export function useOpenCloudPath(): (cloudPath: string) => void {
  const navigate = useNavigate()
  const layout = useLayoutPanelsOptional()
  return useCallback(
    (cloudPath: string) => {
      const {rel, isFolder} = parseCloudPath(cloudPath)
      if (isFolder) {
        if (layout) layout.openFilesPanelAt(rel)
        else navigate(cloudPathHref(cloudPath))
        return
      }
      void openFileInViewer({
        filename: rel,
        displayName: cloudPathName(cloudPath),
        navigate,
        expandCenter: layout?.expandCenter,
      })
    },
    [navigate, layout]
  )
}

function isPlainLeftClick(e: React.MouseEvent): boolean {
  return !(e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
}

type CloudPathLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  /** A `cloud:` path; an invalid one renders its children as plain text. */
  path: string
  /**
   * The answer-text look: an icon for what it points at (folder, result, report, …) and a chip
   * style (`.omics-cloud-link--chip`). Off in lists that bring their own icons (Files changed).
   */
  decorated?: boolean
}

/** A link to a file or folder in the user's cloud storage, opened in-app on a plain click. */
export const CloudPathLink: React.FC<CloudPathLinkProps> = ({
  path,
  children,
  onClick,
  decorated = false,
  ...props
}) => {
  const open = useOpenCloudPath()
  const parsed = tryParseCloudPath(path)
  if (!parsed) {
    return <span className='omics-cloud-link omics-cloud-link--invalid'>{children}</span>
  }
  const kind = decorated ? cloudLinkKind(parsed.cloud) : null
  return (
    <a
      {...props}
      // Always the canonical path: a markdown link title must not disguise where the link goes.
      title={parsed.cloud}
      className={clsx(
        'omics-cloud-link',
        kind && `omics-cloud-link--chip omics-cloud-link--${kind}`,
        props.className
      )}
      href={cloudPathHref(parsed.cloud)}
      onClick={(e) => {
        onClick?.(e)
        if (!isPlainLeftClick(e)) return
        e.preventDefault()
        open(parsed.cloud)
      }}
    >
      {kind ? (
        <i className={`fas ${CLOUD_LINK_ICONS[kind]} omics-cloud-link__icon`} aria-hidden />
      ) : null}
      {children}
    </a>
  )
}
